import type { MessageKey, Translate } from "../i18n";
import { TinkrApiError } from "./tinkr-api";

/**
 * Turns the structured part of an API error into the sentence a user can
 * act on. Until the 2026-09-07 sweep every panel's `messageFromApiError`
 * fell through to `error.message`, which for a database rejection was
 * "Internal server error" and for a validation failure was a generic
 * "Invalid … request" even though the API had put the field name in
 * `details.fieldErrors`.
 *
 * Returns `null` when the error carries nothing more specific than its
 * message, so callers keep their existing fallback.
 *
 * Sources, in order:
 *   1. `constraint_violation` / `duplicate_value` (PR #1473): known
 *      database rules get dedicated copy; unknown identifiers stay hidden.
 *   2. `validation_failed` with `details.fieldErrors` (or zod's
 *      `details.issues.fieldErrors`): "field: message" for each field.
 *   3. Wrapper codes whose libs collapse an RPC rejection into one generic
 *      sentence (`job_command_rejected`, `provider_shipping_rates_write_rejected`,
 *      …): known rejection codes get actionable translated copy; unknown
 *      codes use a localized fallback. Raw backend prose is not presented
 *      as though it were translated.
 */

const CONSTRAINT_COPY: Record<string, MessageKey> = {
  provider_applications_individual_showcase_check: "apiError.constraint.showcaseUrl",
  provider_shipping_rate_configs_active_lane_idx: "apiError.constraint.shippingLaneDuplicate",
  provider_material_rates_pkey: "apiError.constraint.materialDuplicate",
  announcements_locale_check: "apiError.constraint.localeTag",
  in_app_notifications_locale_check: "apiError.constraint.localeTag",
  announcements_time_check: "apiError.constraint.endAfterStart",
  maintenance_events_time_check: "apiError.constraint.endAfterStart",
  platform_status_events_time_check: "apiError.constraint.endAfterStart",
  enforcement_actions_time_check: "apiError.constraint.expiryAfterEffective",
  platform_flags_updated_reason_check: "apiError.constraint.reasonLength",
  ppa_app_url_check: "apiError.constraint.httpUrl",
  ppa_terms_url_check: "apiError.constraint.httpUrl",
  ppa_privacy_url_check: "apiError.constraint.httpUrl",
  partner_webhook_subscriptions_endpoint_url_check: "apiError.constraint.webhookEndpoint",
  provider_identity_verifications_return_url_check: "apiError.constraint.httpsUrl",
  in_app_notifications_action_url_check: "apiError.constraint.urlNoSpaces",
  providers_user_id_key: "apiError.constraint.providerExists",
  provider_applications_user_id_key: "apiError.constraint.applicationExists"
};

const KIND_COPY: Record<string, MessageKey> = {
  foreign_key_violation: "apiError.kind.foreignKey",
  value_too_long: "apiError.kind.tooLong",
  invalid_value: "apiError.kind.invalidValue"
};

/**
 * Wrapper error codes whose `details.rejectionCode` carries the real
 * reason. Keyed by wrapper code, then rejection code.
 */
const REJECTION_COPY: Record<string, Record<string, MessageKey>> = {
  job_command_rejected: {
    tracking_required: "apiError.rejection.trackingRequired",
    print_photo_required: "apiError.rejection.printPhotoRequired",
    shipment_already_recorded: "apiError.rejection.shipmentAlreadyRecorded",
    shipment_locked: "apiError.rejection.shipmentLocked",
    shipment_not_recorded: "apiError.rejection.shipmentNotRecorded",
    idempotency_conflict: "apiError.rejection.idempotencyConflict",
    invalid_state: "apiError.rejection.invalidState",
    order_payment_required: "apiError.rejection.orderPaymentRequired",
    engagement_not_found: "apiError.rejection.engagementNotFound"
  },
  provider_shipping_rates_write_rejected: {
    invalid_shipping_rates: "apiError.rejection.invalidShippingRates"
  },
  provider_rate_card_write_rejected: {
    invalid_rate_card_payload: "apiError.rejection.invalidRateCard"
  },
  partner_webhook_subscription_rejected: {
    endpoint_url_required: "apiError.rejection.endpointUrlRequired",
    event_types_required: "apiError.rejection.eventTypesRequired",
    signing_secret_required: "apiError.rejection.signingSecretRequired",
    subscription_not_found: "apiError.rejection.subscriptionNotFound"
  },
  partner_production_application_rejected: {
    production_review_pending: "apiError.rejection.productionReviewPending",
    invalid_application_fields: "apiError.rejection.invalidApplicationFields",
    partner_identity_not_verified: "apiError.rejection.partnerIdentityNotVerified",
    partner_business_review_not_approved: "apiError.rejection.partnerBusinessReviewNotApproved",
    already_production: "partnerLifecycle.error.alreadyProduction"
  },
  partner_governance_transition_rejected: {
    identity_refresh_throttled: "apiError.rejection.identityRefreshThrottled"
  },
  first_party_app_write_rejected: {
    invalid_origin: "apiError.rejection.invalidOrigin",
    app_already_exists: "apiError.rejection.appAlreadyExists",
    duplicate_origin_environment: "apiError.rejection.duplicateOriginEnvironment",
    app_origin_required: "apiError.rejection.appOriginRequired",
    active_request_key: "apiError.rejection.activeRequestKey"
  }
};

const VALIDATION_FIELD_COPY: Record<string, MessageKey> = {
  endsAt: "apiError.constraint.endAfterStart",
  trackingUrl: "apiError.constraint.httpUrl",
  endpointUrl: "apiError.constraint.webhookEndpoint",
  returnUrl: "apiError.constraint.httpsUrl",
  showcaseUrl: "apiError.constraint.showcaseUrl"
};

function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(value),
    template
  );
}

function detailsOf(error: TinkrApiError): Record<string, unknown> | null {
  return error.details && typeof error.details === "object"
    ? (error.details as Record<string, unknown>)
    : null;
}

function stringField(details: Record<string, unknown> | null, key: string): string | null {
  const value = details?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/**
 * Field-level messages from a `validation_failed` envelope, keyed by field
 * path. Empty when the API sent none. Exported so forms can render each
 * message under its own input instead of one joined line.
 */
export function fieldErrorsFromApiError(error: unknown): Record<string, string[]> {
  if (!(error instanceof TinkrApiError)) return {};
  const details = detailsOf(error);
  const direct = details?.fieldErrors;
  const nested =
    details?.issues && typeof details.issues === "object"
      ? (details.issues as { fieldErrors?: unknown }).fieldErrors
      : undefined;
  const source = direct ?? nested;
  if (!source || typeof source !== "object") return {};
  const result: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(source as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      const clean = messages.filter((m): m is string => typeof m === "string" && m.length > 0);
      if (clean.length > 0) result[field] = clean;
    }
  }
  return result;
}

export function describeApiError(error: unknown, t: Translate): string | null {
  if (!(error instanceof TinkrApiError)) return null;
  const details = detailsOf(error);

  // A structured 403 from an RPC actor raise (PR #1473 emits it as code
  // `forbidden` with `details.kind === "not_permitted"` and the snake_case
  // reason). Checked first so a panel's generic "no access" copy does not
  // pre-empt it; a plain `forbidden` without the structured kind returns
  // null and keeps the panel's own 403 handling.
  if (error.code === "forbidden" && stringField(details, "kind") === "not_permitted") {
    const reason = stringField(details, "reason");
    return reason === "actor_not_active"
      ? t("apiError.permission.actorInactive")
      : t("apiError.kind.generic");
  }

  if (error.code === "constraint_violation" || error.code === "duplicate_value") {
    const constraint = stringField(details, "constraint");
    if (constraint && CONSTRAINT_COPY[constraint]) {
      return t(CONSTRAINT_COPY[constraint]);
    }
    if (error.code === "duplicate_value") {
      return t("apiError.duplicateGeneric");
    }
    const column = stringField(details, "column");
    if (stringField(details, "kind") === "not_null_violation" && column) {
      const dedicated = VALIDATION_FIELD_COPY[column];
      return dedicated ? t(dedicated) : t("apiError.validation.generic");
    }
    const kind = stringField(details, "kind");
    if (kind && KIND_COPY[kind]) return t(KIND_COPY[kind]);
    return t("apiError.kind.generic");
  }

  if (error.code === "validation_failed") {
    const fields = Object.keys(fieldErrorsFromApiError(error)).slice(0, 4);
    if (fields.length === 0) return t("apiError.validation.generic");
    return fields
      .map((fieldPath) => {
        const field = fieldPath.split(".").pop() ?? fieldPath;
        const dedicated = VALIDATION_FIELD_COPY[field];
        return dedicated ? t(dedicated) : t("apiError.validation.generic");
      })
      .filter((message, index, messages) => messages.indexOf(message) === index)
      .join(" ");
  }

  if (error.code === "provider_offer_command_rejected" && stringField(details, "rejectionCode") === "provider_at_capacity") {
    return t("orderLimit.full");
  }

  // ADR premium §4.3: the accept transition wraps premium admission as
  // details.rejectionCode. Surfaces that accept offers outside the work queue
  // (the provider home panel) get the same copy as the work queue.
  if (error.code === "provider_offer_command_rejected" && stringField(details, "rejectionCode") === "premium_not_admitted") {
    return t("workQueue.error.premiumNotAdmitted");
  }
  // A quote priced before the job was relaxed to standard (or vice versa).
  if (error.code === "provider_offer_command_rejected" && stringField(details, "rejectionCode") === "quote_premium_mismatch") {
    return t("workQueue.error.quotePremiumMismatch");
  }

  // A write from a provider whose record is not active (suspended, closed)
  // is rejected by the transition function under several wrapper codes
  // (rate cards, capabilities, guided pricing). Say why instead of the
  // generic "couldn't complete" copy (PRC-9).
  if (stringField(details, "rejectionCode") === "provider_not_mutable") {
    return t("apiError.rejection.providerNotMutable");
  }

  const rejectionMap = REJECTION_COPY[error.code];
  if (rejectionMap) {
    const rejectionCode = stringField(details, "rejectionCode") ?? "unknown";
    const key = rejectionMap[rejectionCode];
    if (key) return t(key);
    return t("apiError.rejection.unknown");
  }

  return null;
}

/** Add the API trace id to safe localized copy without exposing backend prose. */
export function withApiErrorRequestId(message: string, error: unknown, t: Translate): string {
  if (!(error instanceof TinkrApiError) || !error.requestId) return message;
  return fill(t("onboarding.error.withRequestId"), {
    message,
    requestId: error.requestId
  });
}

export function describeApiErrorWithFallback(
  error: unknown,
  t: Translate,
  fallback: string
): string {
  return withApiErrorRequestId(describeApiError(error, t) ?? fallback, error, t);
}
